import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

type RefType = {
  current: HTMLElement | null;
};

/**
 * Animates the hero title with a fade-in and slide-up effect
 */
export const animateTitle = (titleRef: RefType, delay: number = 0): void => {
  if (!titleRef.current) return;

  gsap.fromTo(
    titleRef.current,
    { opacity: 0, y: 30 },
    {
      opacity: 1,
      y: 0,
      duration: 1,
      delay,
      ease: "power3.out",
      id: "locationsHero-title",
    }
  );
};

/**
 * Animates the subtitle with a fade-in and slide-up effect, slightly delayed after the title
 */
export const animateSubtitle = (
  subtitleRef: RefType,
  delay: number = 0.5
): void => {
  if (!subtitleRef.current) return;

  gsap.fromTo(
    subtitleRef.current,
    { opacity: 0, y: 20 },
    {
      opacity: 1,
      y: 0,
      duration: 0.8,
      delay,
      ease: "power3.out",
      id: "locationsHero-subtitle",
    }
  );
};

/**
 * Animates the button with a bounce effect
 */
export const animateButton = (
  buttonRef: RefType,
  delay: number = 0.9
): void => {
  if (!buttonRef.current) return;

  gsap.fromTo(
    buttonRef.current,
    { opacity: 0, y: 20, scale: 0.95 },
    {
      opacity: 1,
      y: 0,
      scale: 1,
      duration: 0.8,
      delay,
      ease: "back.out(1.5)",
      id: "locationsHero-button",
    }
  );
};

/**
 * Animates the overlay with a fade-in effect
 */
export const animateOverlay = (overlayRef: RefType): void => {
  if (!overlayRef.current) return;

  gsap.fromTo(
    overlayRef.current,
    { opacity: 0 },
    {
      opacity: 1,
      duration: 1.5,
      ease: "power2.inOut",
      id: "locationsHero-overlay",
    }
  );
};

/**
 * Cleans up only LocationsHero animations
 */
export const cleanupAnimations = (): void => {
  // Only kill animations for this component
  const targets = [
    '[id="locationsHero-title"]',
    '[id="locationsHero-subtitle"]',
    '[id="locationsHero-button"]',
    '[id="locationsHero-overlay"]',
  ];

  targets.forEach((selector) => {
    gsap.killTweensOf(selector);
  });

  // Clean up any ScrollTrigger instances created by this component
  ScrollTrigger.getAll()
    .filter((trigger) => trigger.vars.id?.startsWith("locationsHero"))
    .forEach((trigger) => trigger.kill());
};
